import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  MoreVert,
  KeyboardArrowDown,
  DarkMode,
  LightMode,
  RocketLaunch,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet, isMobileBrowser, isInMetaMaskBrowser, getMetaMaskDeepLink } from '../hooks/useWallet';
import { useThemeMode } from '../hooks/useThemeMode';
import { useWallet as useSolWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useTronWallet } from '../hooks/useTronWallet';
import { MainLogo } from '../components/LogoComponents';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleMode, isDark } = useThemeMode();
  const {
    account,
    connected,
    connectWallet,
    connectWalletConnect,
    disconnectWallet,
    loading
  } = useWallet();
  const isMobile = isMobileBrowser();
  const isInsideMetaMask = isInMetaMaskBrowser();

  const solanaWallet = useSolWallet();
  const { publicKey: solanaPublicKey, connected: solanaConnected, disconnect: disconnectSolana } = solanaWallet;

  // Tron wallet (TronLink only). Strictly isolated from EVM/Solana surfaces.
  const {
    address: tronAddress,
    connected: tronConnected,
    installed: tronInstalled,
    connecting: tronConnecting,
    network: tronNetwork,
    connect: connectTron,
    disconnect: disconnectTron,
  } = useTronWallet();

  const [scrolled, setScrolled] = useState(false);
  const [walletMenuAnchor, setWalletMenuAnchor] = useState({ unified: null });
  // Mobile nav menu for pages not in sidebar
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const isEasyMode = location.pathname === '/easy';

  // Mobile-only nav items (sidebar handles desktop nav)
  const mobileNavItems = [
    { label: 'Quick Privacy', path: '/easy' },
    { label: 'Lock Funds', path: '/deposit' },
    { label: 'My Keys', path: '/dashboard' },
    { label: 'Faucet', path: '/faucet' },
    { label: 'History', path: '/history' },
    { label: 'Leaderboard', path: '/leaderboard' },
    { label: 'Guide', path: '/guide' },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled
            ? isDark ? 'rgba(5, 5, 5, 0.95)' : 'rgba(255, 255, 255, 0.95)'
            : isDark ? 'rgba(5, 5, 5, 0.7)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.3s ease',
          borderBottom: '1px solid',
          borderColor: scrolled
            ? isDark ? '#262626' : '#e5e5e5'
            : isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              cursor: 'pointer',
              gap: 1.5,
            }}
            onClick={() => navigate('/')}
          >
            <MainLogo height={40} />
          </Box>

          {/* Easy / Pro toggle */}
          <Box sx={{ display: 'flex', mr: 1.5, border: '1px solid', borderColor: isDark ? '#262626' : '#e5e5e5', borderRadius: 1, overflow: 'hidden' }}>
            <Chip
              icon={<RocketLaunch sx={{ fontSize: 14 }} />}
              label="Easy"
              size="small"
              onClick={() => navigate('/easy')}
              sx={{
                borderRadius: '4px 0 0 4px',
                height: 32,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: isEasyMode ? 600 : 400,
                bgcolor: isEasyMode
                  ? (isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.12)')
                  : 'transparent',
                color: isEasyMode
                  ? '#22c55e'
                  : (isDark ? '#737373' : '#737373'),
                border: 'none',
                cursor: 'pointer',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Chip
              label="Pro"
              size="small"
              onClick={() => { if (isEasyMode) navigate('/deposit'); }}
              sx={{
                borderRadius: '0 4px 4px 0',
                height: 32,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: !isEasyMode ? 600 : 400,
                bgcolor: !isEasyMode
                  ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                  : 'transparent',
                color: !isEasyMode
                  ? (isDark ? '#e5e5e5' : '#171717')
                  : (isDark ? '#737373' : '#737373'),
                border: 'none',
                cursor: 'pointer',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
              }}
            />
          </Box>

          {/* Mobile menu button (sidebar handles desktop) */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center' }}>
            <IconButton
              color="inherit"
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
              sx={{ color: isDark ? '#a3a3a3' : '#525252', mr: 0.5 }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={() => setMobileMenuAnchor(null)}
            >
              {mobileNavItems.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuAnchor(null);
                  }}
                  selected={location.pathname === item.path}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.85rem',
                    py: 1.5,
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Theme toggle */}
          <IconButton
            onClick={toggleMode}
            sx={{
              mr: 1,
              color: isDark ? '#a3a3a3' : '#525252',
              '&:hover': {
                color: isDark ? '#e5e5e5' : '#171717',
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>

          {/* Unified Wallet Dropdown */}
          {(() => {
            const connectedCount = (connected ? 1 : 0) + (solanaConnected && solanaPublicKey ? 1 : 0) + (tronConnected ? 1 : 0);
            return (
              <>
                <Button
                  onClick={(e) => setWalletMenuAnchor({ ...walletMenuAnchor, unified: e.currentTarget })}
                  sx={{
                    background: connectedCount > 0
                      ? isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'
                      : isDark ? '#ffffff' : '#171717',
                    border: '1px solid',
                    borderColor: connectedCount > 0
                      ? isDark ? '#262626' : '#e5e5e5'
                      : isDark ? '#ffffff' : '#171717',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    color: connectedCount > 0
                      ? isDark ? '#e5e5e5' : '#171717'
                      : isDark ? '#050505' : '#ffffff',
                    textTransform: 'none',
                    minWidth: connectedCount > 0 ? 'auto' : { xs: 'auto', sm: '140px' },
                    boxShadow: 'none',
                    '&:hover': {
                      background: connectedCount > 0
                        ? isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                        : isDark ? '#d4d4d4' : '#404040',
                      borderColor: connectedCount > 0
                        ? isDark ? '#404040' : '#d4d4d4'
                        : isDark ? '#d4d4d4' : '#404040',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {connectedCount > 0 ? (
                      <>
                        {connected && (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <Box
                              component="img"
                              src="/logos/metamask.svg"
                              alt="MetaMask"
                              sx={{ width: 22, height: 22 }}
                            />
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e',
                              position: 'absolute', bottom: -2, right: -2,
                              border: '2px solid',
                              borderColor: isDark ? '#050505' : '#fafafa',
                            }} />
                          </Box>
                        )}
                        {solanaConnected && solanaPublicKey && (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <Box
                              component="img"
                              src="/logos/solana.png"
                              alt="Solana"
                              sx={{ width: 22, height: 22 }}
                            />
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e',
                              position: 'absolute', bottom: -2, right: -2,
                              border: '2px solid',
                              borderColor: isDark ? '#050505' : '#fafafa',
                            }} />
                          </Box>
                        )}
                        {tronConnected && tronAddress && (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <Box
                              component="img"
                              src="/logos/tron.svg"
                              alt="Tron"
                              sx={{ width: 22, height: 22, borderRadius: '4px' }}
                            />
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e',
                              position: 'absolute', bottom: -2, right: -2,
                              border: '2px solid',
                              borderColor: isDark ? '#050505' : '#fafafa',
                            }} />
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <AccountBalanceWallet sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', display: { xs: 'none', sm: 'block' } }}>
                          Wallets
                        </Typography>
                      </>
                    )}
                    <KeyboardArrowDown sx={{ fontSize: 18 }} />
                  </Box>
                </Button>
                <Menu
                  anchorEl={walletMenuAnchor.unified}
                  open={Boolean(walletMenuAnchor.unified)}
                  onClose={() => setWalletMenuAnchor({ ...walletMenuAnchor, unified: null })}
                  PaperProps={{
                    sx: {
                      background: isDark ? '#0a0a0a' : '#ffffff',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid',
                      borderColor: isDark ? '#262626' : '#e5e5e5',
                      borderRadius: 1,
                      mt: 1,
                      minWidth: { xs: '260px', sm: '280px' },
                      p: 1,
                    },
                  }}
                >
                  {/* EVM Section */}
                  <Typography sx={{ color: isDark ? '#525252' : '#a3a3a3', fontSize: '0.7rem', fontWeight: 600, px: 1.5, pt: 1, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: '"JetBrains Mono", monospace' }}>
                    EVM Wallet
                  </Typography>
                  {connected ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid',
                        borderColor: isDark ? '#1a1a1a' : '#f0f0f0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          component="img"
                          src="/logos/metamask.svg"
                          alt="MetaMask"
                          sx={{ width: 24, height: 24 }}
                        />
                        <Typography sx={{ color: isDark ? '#a3a3a3' : '#525252', fontWeight: 500, fontSize: '0.9rem' }}>
                          MetaMask
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', color: isDark ? '#e5e5e5' : '#171717' }}>
                          {formatAddress(account)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            disconnectWallet();
                            setWalletMenuAnchor({ ...walletMenuAnchor, unified: null });
                          }}
                          sx={{
                            color: isDark ? '#525252' : '#a3a3a3',
                            p: 0.5,
                            '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '14px', fontWeight: 'bold' }}>&#x2715;</Box>
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: 1,
                          background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid',
                          borderColor: isDark ? '#1a1a1a' : '#f0f0f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                            borderColor: isDark ? '#333' : '#d4d4d4',
                          },
                        }}
                        onClick={() => {
                          if (isMobile && !isInsideMetaMask) {
                            window.location.href = getMetaMaskDeepLink();
                          } else {
                            connectWallet();
                          }
                          setWalletMenuAnchor({ ...walletMenuAnchor, unified: null });
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box component="img" src="/logos/metamask.svg" alt="MetaMask" sx={{ width: 24, height: 24 }} />
                          <Typography sx={{ color: isDark ? '#a3a3a3' : '#525252', fontWeight: 500, fontSize: '0.9rem' }}>
                            MetaMask
                          </Typography>
                        </Box>
                        <Typography sx={{ color: isDark ? '#525252' : '#a3a3a3', fontSize: '0.75rem', fontFamily: '"JetBrains Mono", monospace' }}>
                          {loading ? '...' : (isMobile && !isInsideMetaMask ? 'Open App' : 'Connect')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: 1,
                          background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid',
                          borderColor: isDark ? '#1a1a1a' : '#f0f0f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                            borderColor: isDark ? '#333' : '#d4d4d4',
                          },
                        }}
                        onClick={() => {
                          connectWalletConnect();
                          setWalletMenuAnchor({ ...walletMenuAnchor, unified: null });
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box component="img" src="/logos/walletconnect.svg" alt="WalletConnect" sx={{ width: 24, height: 24, borderRadius: '4px' }} />
                          <Typography sx={{ color: isDark ? '#a3a3a3' : '#525252', fontWeight: 500, fontSize: '0.9rem' }}>
                            WalletConnect
                          </Typography>
                        </Box>
                        <Typography sx={{ color: isDark ? '#525252' : '#a3a3a3', fontSize: '0.75rem', fontFamily: '"JetBrains Mono", monospace' }}>
                          Connect
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Solana Section */}
                  <Typography sx={{ color: isDark ? '#525252' : '#a3a3a3', fontSize: '0.7rem', fontWeight: 600, px: 1.5, pt: 1.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: '"JetBrains Mono", monospace' }}>
                    Solana Wallets
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 1,
                      background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid',
                      borderColor: isDark ? '#1a1a1a' : '#f0f0f0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box component="img" src="/logos/solana.png" alt="Solana" sx={{ width: 24, height: 24 }} />
                      <Typography sx={{ color: isDark ? '#a3a3a3' : '#525252', fontWeight: 500, fontSize: '0.9rem' }}>
                        Solana
                      </Typography>
                    </Box>
                    {solanaConnected && solanaPublicKey ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', color: isDark ? '#e5e5e5' : '#171717' }}>
                          {formatAddress(solanaPublicKey.toString())}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (disconnectSolana) await disconnectSolana();
                            setWalletMenuAnchor({ ...walletMenuAnchor, unified: null });
                          }}
                          sx={{
                            color: isDark ? '#525252' : '#a3a3a3',
                            p: 0.5,
                            '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '14px', fontWeight: 'bold' }}>&#x2715;</Box>
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{
                        '& .wallet-adapter-button': {
                          background: isDark ? '#ffffff' : '#171717',
                          color: isDark ? '#050505' : '#ffffff',
                          borderRadius: '2px',
                          padding: '4px 16px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          border: 'none',
                          fontFamily: 'inherit',
                          minWidth: 'auto',
                          height: 'auto',
                          '&:hover': {
                            background: isDark ? '#d4d4d4' : '#404040',
                          },
                        },
                      }}>
                        <WalletMultiButton />
                      </Box>
                    )}
                  </Box>

                  {/* Tron Section (TronLink only). Strictly isolated — clicking
                       Connect here triggers ONLY TronLink, never MetaMask or Phantom. */}
                  <Typography sx={{ color: isDark ? '#525252' : '#a3a3a3', fontSize: '0.7rem', fontWeight: 600, px: 1.5, pt: 1.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: '"JetBrains Mono", monospace' }}>
                    Tron Wallets
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 1,
                      background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid',
                      borderColor: isDark ? '#1a1a1a' : '#f0f0f0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box component="img" src="/logos/tron.svg" alt="Tron" sx={{ width: 24, height: 24, borderRadius: '4px' }} />
                      <Typography sx={{ color: isDark ? '#a3a3a3' : '#525252', fontWeight: 500, fontSize: '0.9rem' }}>
                        Tron {tronNetwork === 'tron-nile' ? '(Nile)' : tronNetwork === 'tron-shasta' ? '(Shasta)' : ''}
                      </Typography>
                    </Box>
                    {tronConnected && tronAddress ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', color: isDark ? '#e5e5e5' : '#171717' }}>
                          {formatAddress(tronAddress)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            disconnectTron();
                            setWalletMenuAnchor({ ...walletMenuAnchor, unified: null });
                          }}
                          sx={{
                            color: isDark ? '#525252' : '#a3a3a3',
                            p: 0.5,
                            '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '14px', fontWeight: 'bold' }}>&#x2715;</Box>
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        onClick={async () => {
                          try {
                            await connectTron();
                          } catch (err) {
                            console.warn('TronLink connect failed:', err.message);
                          }
                        }}
                        disabled={tronConnecting}
                        sx={{
                          background: isDark ? '#ffffff' : '#171717',
                          color: isDark ? '#050505' : '#ffffff',
                          borderRadius: '2px',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          minWidth: 'auto',
                          '&:hover': { background: isDark ? '#d4d4d4' : '#404040' },
                          '&.Mui-disabled': { background: isDark ? '#404040' : '#a3a3a3', color: isDark ? '#1a1a1a' : '#e5e5e5' },
                        }}
                      >
                        {tronConnecting ? '...' : tronInstalled ? 'Connect' : 'Install'}
                      </Button>
                    )}
                  </Box>
                </Menu>
              </>
            );
          })()}
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* Spacer for fixed header */}
    </>
  );
};

export default Header;
